import {useState, useEffect} from 'react'
import  { v4 as uuidv4 } from 'uuid';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { Button, Divider, Paper } from '@mui/material';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import { getCategoriesList, categoriesCreate, canDeleteSubByCategory } from '../services/categoriesService';
import DeleteModal from '../components/DeleteModal';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { useSnackbar } from 'notistack';
import SubcategoryNameModal from '../components/SubcategoryNameModal';
import ShowLoading from '../components/ShowLoading';

const getLargestNumber = (list, attribute) => {
    if (!list || list.length === 0) {
      return 0;
    }
  
    let largestNumber = 0;
  
    list.forEach((item) => {
      const Name = item[attribute];
      const NameSplit = Name.split(' ');
      const number = parseInt(NameSplit[NameSplit.length-1]);
  
      if (!isNaN(number) && number > largestNumber) {
        largestNumber = number;
      }
    });
  
    return largestNumber;
};

const defaultCategories = [
    'Productos',
    'Marcas',
    'Aplicaciones',
    'Magnitudes'
];


const CategoriesPage = () => {

    const { enqueueSnackbar } = useSnackbar();
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [subByCategories, setSubByCategories] = useState([]);
    const [categoriesToDelete, setCategoriesToDelete] = useState([]);
    const [subcategoriesToDelete, setSubcategoriesToDelete] = useState([]);
    const [subByCategoriesToDelete, setSubByCategoriesToDelete] = useState([]);
    const [subcategoriesToRename, setSubcategoriesToRename] = useState([]);

    const [openModal, setOpenModal] = useState(false);
    const [ openNameModal, setOpenNameModal ] = useState(false)
    const [deleteTitle, setDeleteTitle] = useState('');
    const [itemToDelete, setItemToDelete] = useState('');
    const [itemToEdit, setItemToEdit] = useState()
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() =>{
        getCategoriesData();
    }, []);

    const getCategoriesData = async() => {
        const { categories, subCategories, subByCategories } = await getCategoriesList();
        const newCategories = addDefaultCategories(categories?.items);

        // console.log("FETCHED CATEGORIES", categories, subCategories, subByCategories);

        setSubcategories(subCategories?.items.map(item => ({...item, editable: false})));
        setCategories(newCategories.map(item => ({...item, editable: false})));
        setSubByCategories(subByCategories?.items?.map(item => ({...item, editable: false})));
    };

    const addDefaultCategories = (categoriesList) => {
        const categoriesNames = categoriesList.map(category => category.categoryName);
        const unAddedCategories = defaultCategories.filter(item => !categoriesNames.includes(item));
        const newDefaultCategories =  unAddedCategories.map(category => 
            ({_id: uuidv4(), categoryName: category, subcategoriesId: [],  _status: false, deleted: false, isNewRecord: true, editable: false})
        );
        return [...categoriesList, ...newDefaultCategories];
    };

    const addSubCategory = () => {
        const lastNumber = getLargestNumber(subcategories, 'subCategoryName');
        const NewSub = {_id: uuidv4(), subCategoryName: `nueva subcategoría ${lastNumber + 1}`, _status: false, deleted: false, isNewRecord: true, description: ''};
        setSubcategories([
            ...subcategories,
            NewSub
        ]);

        onOpenNameModal(NewSub)
        return NewSub
    };

    const addCategory = async () => {
        const lastNumber = getLargestNumber(categories, 'categoryName');
        const NewCategory = {_id: uuidv4(), categoryName: `nueva categoría ${lastNumber + 1}`, subcategoriesId: [],  _status: false, deleted: false, isNewRecord: true, editable: false};
        setCategories([
            ...categories,
            NewCategory
        ]);

        // Add new category to items to save
        await onSave({categories: [...categories, NewCategory]});
    };

    const addSubByCategories = (categoryId, subCategoryId) => {
        const NewSubByCategory = {_id: uuidv4(), categoryId, subCategoryId, index: 1 ,  isNewRecord: true, editable: false};
        setSubByCategories([
            ...subByCategories,
            NewSubByCategory
        ]);
        return NewSubByCategory;
    };

    const addSubToCategory = (categoryId) => {
        // Add new subcategory to subcategories
        const newSub = addSubCategory()

        // Add subcategory to SubByCat
        const newSubByCategories = addSubByCategories(categoryId, newSub._id)

        // Add category and SubByCategory to category.categoriesId
        const destinationCategory = categories.find((item) => item._id === categoryId)
        if (!destinationCategory) return

        // console.log("TARGET", destinationCategory)
        const newSubCategoryEntry = {
            _id: uuidv4(),
            subcategoryId: newSub._id,
            subByCategoriesId: newSubByCategories._id
        }

        const updatedCategory = {
            ...destinationCategory,
            subcategoriesId: [
                ...(destinationCategory.subcategoriesId || []),
                newSubCategoryEntry
            ]
        }

        const updatedCategories = categories.map((item) => item._id === updatedCategory._id ? updatedCategory : item)
        setCategories(updatedCategories)
    }

    const deleteSubCategory = async () => {
        const subByCategoryIds = subByCategories.filter(subBy => subBy.subCategoryId == itemToDelete.item._id && !subBy.isNewRecord).map(item => item._id);
    
        if(!!subByCategoryIds.length){
            const canDelete = await canDeleteSubByCategory(subByCategoryIds);
            if(!canDelete.result){
                enqueueSnackbar("No se puede eliminar porque está asociado a un producto.", {
                    variant: 'warning'
                });
                return;
            }
        }

        const isRelated = categories.some(category => 
            category.subcategoriesId.some( subInCategory => subInCategory.subcategoryId === itemToDelete.item._id)
        );

        let newSubcategoriesToDelete = [...subcategoriesToDelete];
        
        if(isRelated){
            let newSubByCategoriesToDelete = [];
            let newSubByCategories = [...subByCategories];
            const newCategories = categories.map(category => {
                const newSubcategoriesId = category.subcategoriesId.filter(subInCategory => {
                    if(subInCategory.subcategoryId === itemToDelete.item._id){
                        const subByCategoryItem = subByCategories.find(item => item._id == subInCategory.subByCategoriesId);
                        if(!subByCategoryItem.isNewRecord){
                            newSubByCategoriesToDelete.push(subByCategoryItem._id);
                        }
                        newSubByCategories = newSubByCategories.filter(item => item._id !== subByCategoryItem._id);
                        return false;
                    }
                    return true;
                });
                return {...category, subcategoriesId: newSubcategoriesId }

            });
            setCategories(newCategories);
            setSubByCategories(newSubByCategories);
            setSubByCategoriesToDelete([...subByCategoriesToDelete, ...newSubByCategoriesToDelete])
        }


        if (!itemToDelete?.item?.isNewRecord) {
            newSubcategoriesToDelete.push(itemToDelete.item._id)
            setSubcategoriesToDelete([...newSubcategoriesToDelete]);
        }
        const newSubList = subcategories.filter(sub => sub._id !== itemToDelete.item._id);
        setSubcategories(newSubList);

        return {
            subcategoriesToDelete: newSubcategoriesToDelete
        }
    };

    const deleteCategory = async () => {
        const subByCategoryIds = subByCategories.filter(subBy => subBy.categoryId == itemToDelete.item._id && !subBy.isNewRecord).map(item => item._id);
    
        if(!!subByCategoryIds.length){
            const canDelete = await canDeleteSubByCategory(subByCategoryIds);
            if(!canDelete.result){
                enqueueSnackbar("No se puede eliminar porque está asociado a un producto.", {
                    variant: 'warning'
                });
                return;
            }
        }
        
        const isRelated = !!itemToDelete?.item.subcategoriesId.length;

        let newCategoriesToDelete = [...categoriesToDelete];
        let newSubByCategoriesToDeleteTemp = [...subByCategoriesToDelete];

        if(isRelated){
            let newSubByCategoriesToDelete = [];
            let newSubByCategories = [...subByCategories];

            itemToDelete?.item.subcategoriesId.forEach(subInCategory => {
                const subByCategoryItem = subByCategories.find(item => item._id == subInCategory.subByCategoriesId);
                if(!subByCategoryItem.isNewRecord){
                    newSubByCategoriesToDelete.push(subByCategoryItem._id);
                }
                newSubByCategories = newSubByCategories.filter(item => item._id !== subByCategoryItem._id);
            });

            setSubByCategories(newSubByCategories);
            newSubByCategoriesToDeleteTemp.push(...newSubByCategoriesToDelete);
            setSubByCategoriesToDelete([...newSubByCategoriesToDeleteTemp]);
        }
            
        if (!itemToDelete?.item?.isNewRecord) {
            newCategoriesToDelete.push(itemToDelete.item._id);
            setCategoriesToDelete([...newCategoriesToDelete]);
        }
        const newCategoryList = categories.filter(category => category._id !== itemToDelete.item._id);
        setCategories(newCategoryList);

        return {
            categoriesToDelete: newCategoriesToDelete,
            subByCategoriesToDelete: newSubByCategoriesToDeleteTemp
        }
    };

    const deleteSubByCategory = async () => {
        const subcategory = itemToDelete.item.category.subcategoriesId.find(item => item.subcategoryId == itemToDelete.item.subcategory._id);
        const  subByCategory= subByCategories.find(item => item._id == subcategory.subByCategoriesId);
        if(!subByCategory.isNewRecord){
            const canDelete = await canDeleteSubByCategory([subByCategory._id]);
            if(!canDelete.result){
                enqueueSnackbar("No se puede eliminar porque está asociado a un producto.", {
                    variant: 'warning'
                });
                return;
            }
        }

        let newSubByCategoriesToDelete = [...subByCategoriesToDelete];
        
        const newSubcategoriesId = itemToDelete.item.category.subcategoriesId.filter(subInCategory => {
            if(subInCategory._id === itemToDelete.item._id){
                const subByCategoryItem = subByCategories.find(item => item._id == subInCategory.subByCategoriesId);
                if(!subByCategoryItem.isNewRecord){
                    newSubByCategoriesToDelete.push(subByCategoryItem._id);
                    setSubByCategoriesToDelete([...newSubByCategoriesToDelete]);
                }
                const newSubByCategoriesList = subByCategories.filter(item => item._id !== subByCategoryItem._id);
                setSubByCategories(newSubByCategoriesList);
                return false;
            }
            return true;
        });

        const newCategoryList = categories.map(category =>
            category._id === itemToDelete.item.category._id
                ? { ...category, subcategoriesId: newSubcategoriesId }
                : category
            );

        setCategories(newCategoryList);

        return {
            subByCategoriesToDelete: newSubByCategoriesToDelete
        }
    };

    // Remove items to delete from items to save
    const sanitizeData = (formData) => {
        const { 
            categories, 
            subcategories, 
            subByCategories, 
            categoriesToDelete, 
            subcategoriesToDelete, 
            subByCategoriesToDelete 
        } = formData;
      
        return {
            ...formData,
            categories: categories.filter(c => !categoriesToDelete.includes(c._id)),
            subcategories: subcategories.filter(sc => !subcategoriesToDelete.includes(sc._id)),
            subByCategories: subByCategories.filter(sbc => !subByCategoriesToDelete.includes(sbc._id)),
        };
    };
      
    const onSave =  async (overrides = {}) => {
        if (isLoading) return;

        setIsLoading(true);

        const FormData = { 
            categories: overrides.categories || categories, 
            subcategories: overrides.subcategories || subcategories, 
            subByCategories: overrides.subByCategories || subByCategories, 

            // Override items to delete if provided and deduplicate
            categoriesToDelete: overrides.categoriesToDelete || categoriesToDelete, 
            subcategoriesToDelete: overrides.subcategoriesToDelete || subcategoriesToDelete, 
            subByCategoriesToDelete: overrides.subByCategoriesToDelete || subByCategoriesToDelete, 

            subcategoriesToRename: overrides.subcategoriesToRename || subcategoriesToRename
        };

        const sanitizedFormData = sanitizeData(FormData);

        // console.log("FORM DATA", FormData);
        // console.log("SANITIZED FORM DATA", sanitizedFormData);
        
        const resultForm = await categoriesCreate(sanitizedFormData);

        if(resultForm.result){
        // if (resultForm) {
            enqueueSnackbar("Guardado Exitoso.", {
                variant: 'success',
                style: { backgroundColor: '#FC581F'}
            });

            await getCategoriesData();
            
            // Reset all states
            setSubcategoriesToDelete([]);
            setCategoriesToDelete([]);
            setSubcategoriesToRename([]);
        }else{
            enqueueSnackbar("Ocurrio un error al guardar.", {
                variant: 'error'
            });
        }

        setIsLoading(false);
    };

    const onDeleteModal = (item, type) => {
        setItemToDelete({item, type });
        
        const typeList = {
            subcategory: `subcategoría ${item?.subCategoryName}`,
            category: `categoría ${item?.categoryName}`,
            subByCategory: `relación con la subcategoría ${item?.subcategory?.subCategoryName}`
        }

        setDeleteTitle(`¿Deseas borrar la  ${typeList[type]}`);
        setOpenModal(true);
    };
    
    const onConfirmed = async () => {
        const DeleteMethodList = {
            subcategory: () => deleteSubCategory(),
            category: () => deleteCategory(),
            subByCategory: () => deleteSubByCategory()
        }
        const dataToDelete = await DeleteMethodList[itemToDelete.type]();
        // console.log("DATA TO DELETE", dataToDelete);

        if (dataToDelete) {
            await onSave(dataToDelete);
        }

        onCloseModal();
    };

    const onSubmitNameModal = async (updatedSubcategory) => {
        const updatedSubcategories = subcategories.map(sub => sub._id === updatedSubcategory._id ? updatedSubcategory : sub);

        await onSave({subcategories: updatedSubcategories});
        setSubcategories(updatedSubcategories);

        onCloseNameModal()
    }

    const onOpenNameModal = (item) => {
        // console.log("SET ITEM", item)

        setItemToEdit(item);
        setOpenNameModal(true)
    }

    const onCloseNameModal = () => {
        setOpenNameModal(false);
        setItemToEdit("");
    }

    const onCloseModal = () =>{
        setOpenModal(false);
        setItemToDelete('');
    };

    const getSubcategories = (subcategoriesId) => {
        if(!subcategoriesId.length) return [];
        const subcategoriesName = subcategoriesId.map(({_id, subcategoryId}) => {
            const subcategoryData = subcategories.find((sub) => sub._id === subcategoryId);
            return subcategoryData ? {_id, subcategory: subcategoryData } : null;
        });
        return subcategoriesName.filter(item => item);
    };

    const updateField = (isCategories, index, field, value) => {
        const cloneList = isCategories ? Array.from(categories): Array.from(subcategories) ;
        if(field == 'subCategoryName' && !cloneList[index].isNewRecord){
            let cloneSubsToRename = Array.from(subcategoriesToRename);
            const indexRecord = subcategoriesToRename.findIndex(item => item.newValue == cloneList[index][field]);
            if(indexRecord >= 0){
                cloneSubsToRename[indexRecord].newValue = value;
            }else{
                cloneSubsToRename.push({oldValue: cloneList[index][field], newValue: value})
            }
            setSubcategoriesToRename(cloneSubsToRename)
        }
        cloneList[index][field] = value;
        isCategories ? setCategories(cloneList) : setSubcategories(cloneList);
	};

    const handleKeyDown = ({ isCategories, index, value, key }) => {
        if (key === 'Enter' && value.trim()) {
          updateField(isCategories, index, isCategories ? 'categoryName': 'subCategoryName', value);
        }
        
        if (key === 'Enter' || key === 'Escape') {
          updateField(isCategories, index, 'editable', false);
        }
    };

    return (
        <> 
            <CssBaseline>
                <Container maxWidth="xl" sx={{ height: '100%', px: '1rem !important', pt: '2%'}}>
                    <Grid container sx={{pb: 3}}>
                        <Grid item xs={12}>
                            <Typography variant='h4' component='h1' sx={{fontSize: '1.5rem', lineHeight: '2rem' }}>
                            <strong>CATEGORÍAS</strong>
                            </Typography>
                        </Grid>
                        {isLoading && (<ShowLoading />)}
                        {!isLoading && (
                            <Grid item container xs={12} spacing={4} sx={{pb: '3.6%', mt: 0, overflowY: 'auto' }}>
                                { categories?.map((category, index) => (
                                    <>
                                    {/* Create grid tables */}
                                    <Grid key={index} item xs={12} md={4}>
                                        <Box 
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "flex-start",
                                                width: "100%",
                                                boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.1), 1px 1px 4px rgba(0, 0, 0, 0.15)',
                                                borderRadius: '4px',
                                                height: '28rem',
                                                backgroundColor: 'white',
                                                p: ".25rem",
                                            }}
                                            component={Paper}
                                        >
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    justifyContent: "space-between",
                                                    alignItems: "center"
                                                }}
                                            >                                            
                                                {category.editable 
                                                    ? <FormControl variant="standard" fullWidth>
                                                        <TextField autoFocus inputProps={{style:{fontSize: '13px'}}}
                                                            size='small'
                                                            name='categoryName' 
                                                            defaultValue={category.categoryName}
                                                            onKeyDown={event => handleKeyDown({ isCategories: true, index, value: event.target.value, key: event.key })}
                                                            onBlur={event => handleKeyDown({ isCategories: true, index, value: event.target.value, key: 'Enter' })}
                                                        />
                                                        </FormControl>
                                                    : <Typography variant='h7' component='h2' sx={{fontSize: '13px', color: '#A1A1A1'}}>
                                                        <strong>{category.categoryName}</strong>
                                                    </Typography>
                                                }

                                                <Box display={"flex"} flexDirection={"row"} alignItems={"center"} gap={2}>
                                                    <AddIcon 
                                                        sx={{
                                                            color: '#FC581F',
                                                            '&:hover': { cursor: 'grab', backgroundColor: '#A1A1A1'},
                                                            borderRadius: "16rem"
                                                        }} 

                                                        onClick={() => {addSubToCategory(category._id)}}
                                                    />
                                                    <CancelIcon
                                                        sx={{
                                                            p: 0,
                                                            color: '#FC581F',
                                                            opacity: defaultCategories.includes(category.categoryName) ? 0.5 : 1, 
                                                            fontSize: '1.3rem'
                                                        }}

                                                        onClick={() =>  !defaultCategories.includes(category.categoryName) && onDeleteModal(category, 'category')}
                                                    />
                                                </Box>
                                            </Box>
                                            <Divider variant="middle" sx={{borderColor: "black", mx: 0, my: '1%'}}/>
                                            <Box 
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "flex-start",
                                                    width: "100%",
                                                    maxHeight: "24rem",
                                                    overflowY: "scroll"
                                                }}
                                            >
                                                {!!category?.subcategoriesId?.length && getSubcategories(category.subcategoriesId).map(({_id, subcategory }, index) => (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex", 
                                                            flexDirection: "row", 
                                                            justifyContent: "space-between", 
                                                            alignItems: "center", 
                                                            flexWrap: "nowrap",
                                                            borderBottom: 1,
                                                            borderColor: '#A1A1A1',
                                                            pr: 2
                                                        }}
                                                    >
                                                        <Typography variant='h6' component='h5' sx={{fontSize: '13px', padding: '0.6rem'}}>
                                                            {subcategory.subCategoryName}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: "row",
                                                                gap: 2,
                                                                alignItems: "center"
                                                            }}
                                                        >
                                                            <EditOutlinedIcon 
                                                                style={{ fontSize: {lg:'1.5rem'}, color: 'black'}}
                                                                sx={{'&:hover': { cursor: 'grab'}}}
                                                                onClick={() => onOpenNameModal(subcategory)}
                                                            />
                                                            <CancelIcon
                                                                sx={{
                                                                    color:'#FC581F'
                                                                }}
                                                                onClick={() => onDeleteModal(subcategory, 'subcategory')}
                                                            />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    </>
                                ))}
                                <Grid item xs={12} md={4} sx={{display: 'flex'}}>
                                    <Box
                                        sx={{
                                            boxShadow: '0px 0px 1px rgba(0, 0, 0, 0.1), 1px 1px 4px rgba(0, 0, 0, 0.15)',
                                            borderRadius: '4px',
                                            minHeight: '13.75rem',
                                            width: '100%',
                                            backgroundColor: 'white'
                                        }}
                                    >   
                                        <Grid container sx={{height: '100%', display: 'flex'}}>
                                            <Grid item xs={12} sx={{ px: '1.2%', pt: '1.2%'}}>
                                                <Typography variant='h7' component='h2' sx={{fontSize: '13px', color: '#A1A1A1'}}>
                                                    <strong>NUEVA CATEGORÍA</strong>
                                                </Typography>
                                                <Divider variant="middle" sx={{mx: 0, my: '1%'}}/>
                                            </Grid>
                                            <Grid item xs={12} sx={{display: 'flex' , height:'100%'}} justifyContent="center" alignItems="center" onClick={addCategory}>
                                                <Typography variant="button" component={Button} startIcon={<AddIcon  sx={{color: '#FC581F'}}/>} sx={{fontSize: '16px', color: 'black', textTransform: 'none'}}>
                                                    <strong>
                                                        Agregar Categoría
                                                    </strong>
                                                </Typography> 
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                        
                        <Grid item xs={11}/>
                        <Grid item xs={1} sx={{display: 'flex', pt: '1rem'}} justifyContent="flex-end">
                            <Button
                                fullWidth
                                sx={{ 
                                    backgroundColor: '#FC581F',
                                    borderRadius: '4px',
                                    textTransform: 'none',
                                    '&:hover': { backgroundColor: '#FC581F'}
                                }} 
                                variant="contained"
                                onClick={onSave}
                            >
                                Guardar
                            </Button>
                        </Grid>
                    </Grid>
                </Container>
                <DeleteModal 
                    title={deleteTitle} 
                    confirmButtonText={'Eliminar'} 
                    onConfirmed={onConfirmed} 
                    openModal={openModal} 
                    onCloseModal={onCloseModal}
                    isDisabled={isLoading}
                />
                <SubcategoryNameModal 
                    itemToEdit={itemToEdit} 
                    openNameModal={openNameModal} 
                    onSubmitNameModal={onSubmitNameModal} 
                    onCloseNameNodal={onCloseNameModal} 
                    isDisabled={isLoading}
                />
            </CssBaseline>
        </>
    )
}

export default CategoriesPage